import { useState, type ReactElement } from 'react';
import {
  FeatureTestRunsNestedTable,
  type FeatureTestRunActions,
} from '@/features/features/components/FeatureTestRunsNestedTable';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import type { ReleaseDeliveryMode } from '@/features/releases/pages/releasesTableDefinition';
import type { TableV2Counts } from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { TagColor } from '@/shared/data/tags';
import type { FeatureDto, ReleaseListItemDto } from '@flowqa/shared';

type ReleaseFeatureItem = NonNullable<NonNullable<ReleaseListItemDto['features']>['items']>[number];
type ReleaseFeatureTableDateMode = ReleaseDeliveryMode | 'all';

type ReleaseFeaturesNestedTableProps = {
  deleteRowPendingId: string | null;
  expanded: boolean;
  features: ReleaseFeatureItem[];
  projectId: string;
  releaseMode: ReleaseFeatureTableDateMode;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: FeatureDto) => void;
  onEditRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
};

const buildReleaseFeaturesColgroup = (releaseMode: ReleaseFeatureTableDateMode): ReactElement => (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="22%" />
    <col width="12%" />
    <col width="15%" />
    {releaseMode === 'all' ? <col width="15%" /> : null}
    <col width="21%" />
    <col width="15%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const resolveReleaseFeaturesColumnCount = (releaseMode: ReleaseFeatureTableDateMode): number =>
  releaseMode === 'all' ? 8 : 7;

const buildReleaseFeaturesHeader = (releaseMode: ReleaseFeatureTableDateMode): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2HeaderLabel label="" />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Feature" />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Status" />
    </TableCell>
    {releaseMode !== 'released' ? (
      <TableCell>
        <TableV2HeaderLabel label="Planned For" />
      </TableCell>
    ) : null}
    {releaseMode !== 'inProgress' ? (
      <TableCell>
        <TableV2HeaderLabel label="Released At" />
      </TableCell>
    ) : null}
    <TableCell>
      <TableV2HeaderLabel label="Testing Status" />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const toNestedFeatureActionRow = (
  feature: ReleaseFeatureItem,
  fallbackProjectId: string,
): FeatureDto => ({
  id: feature.id,
  slug: feature.slug,
  name: feature.name,
  status: feature.status,
  plannedFor: feature.plannedFor ?? null,
  releasedAt: feature.releasedAt ?? null,
  projectId: feature.projectId ?? fallbackProjectId,
  testLatestRunPassed: feature.testLatestRunPassed ?? 0,
  testLatestRunFailed: feature.testLatestRunFailed ?? 0,
  testLatestRunErrored: feature.testLatestRunErrored ?? 0,
  testLatestRunSkipped: feature.testLatestRunSkipped ?? 0,
  testLatestRunUntested: feature.testLatestRunUntested ?? 0,
  tags: feature.tags ?? [],
  deletedAt: feature.deletedAt ?? null,
});

const buildFeatureTestCounts = (feature: ReleaseFeatureItem): TableV2Counts => ({
  total:
    (feature.testLatestRunPassed ?? 0) +
    (feature.testLatestRunFailed ?? 0) +
    (feature.testLatestRunErrored ?? 0) +
    (feature.testLatestRunSkipped ?? 0) +
    (feature.testLatestRunUntested ?? 0),
  passed: feature.testLatestRunPassed ?? 0,
  failed: feature.testLatestRunFailed ?? 0,
  errored: feature.testLatestRunErrored ?? 0,
  skipped: feature.testLatestRunSkipped ?? 0,
  untested: feature.testLatestRunUntested ?? 0,
});

const ReleaseFeatureNestedRow = ({
  deleteRowPendingId,
  feature,
  isLastVisibleRow,
  projectId,
  releaseMode,
  tagColorByName,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  feature: ReleaseFeatureItem;
  isLastVisibleRow: boolean;
  projectId: string;
  releaseMode: ReleaseFeatureTableDateMode;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: FeatureDto) => void;
  onEditRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const actionRow = toNestedFeatureActionRow(feature, projectId);
  const testCounts = buildFeatureTestCounts(feature);

  return (
    <>
      <TableRow
        sx={buildTableV2NestedBodyRowSx({
          depth: 1,
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse feature test runs' : 'Expand feature test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell href={`/features/${feature.slug}`} primary={feature.name} />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={feature.status} />
        </TableCell>
        {releaseMode !== 'released' ? (
          <TableCell>
            <TableV2DateCell value={feature.plannedFor ?? null} />
          </TableCell>
        ) : null}
        {releaseMode !== 'inProgress' ? (
          <TableCell>
            <TableV2DateCell value={feature.releasedAt ?? null} />
          </TableCell>
        ) : null}
        <TableCell>
          <TableV2PassRateCell counts={testCounts} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={feature.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={feature.name}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Feature')}
            editTagsEntityNoun="feature"
            rowTags={feature.tags ?? []}
            onEdit={() => {
              onEditRow(actionRow);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row: actionRow, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === feature.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Feature')}
            onDelete={() => {
              onDeleteRow(actionRow);
            }}
            disableDelete={deleteRowPendingId === feature.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={resolveReleaseFeaturesColumnCount(releaseMode)}>
          <FeatureTestRunsNestedTable
            childCount={testCounts.total}
            depth={2}
            expanded={expanded}
            featureId={feature.id}
            projectId={projectId}
            tagColorByName={tagColorByName}
            testRunActions={testRunActions}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const ReleaseFeaturesNestedTable = ({
  deleteRowPendingId,
  expanded,
  features,
  projectId,
  releaseMode,
  tagColorByName,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: ReleaseFeaturesNestedTableProps): ReactElement => (
  <TableV2NestedCollapse expanded={expanded}>
    <Box sx={tableV2NestedContentSx}>
      {features.length > 0 ? (
        <TableV2NestedFrame depth={1}>
          <Table aria-label="Release features" sx={tableV2NestedDepthSx[1].table}>
            {buildReleaseFeaturesColgroup(releaseMode)}
            <TableHead>{buildReleaseFeaturesHeader(releaseMode)}</TableHead>
            <TableBody>
              {features.map((feature, index) => (
                <ReleaseFeatureNestedRow
                  key={feature.id}
                  deleteRowPendingId={deleteRowPendingId}
                  feature={feature}
                  isLastVisibleRow={index === features.length - 1}
                  projectId={projectId}
                  releaseMode={releaseMode}
                  tagColorByName={tagColorByName}
                  testRunActions={testRunActions}
                  updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                  onDeleteRow={onDeleteRow}
                  onEditRow={onEditRow}
                  onUpdateRowTagsColor={onUpdateRowTagsColor}
                />
              ))}
            </TableBody>
          </Table>
        </TableV2NestedFrame>
      ) : (
        <TableV2NestedEmptyState
          ariaLabel="Release features"
          colgroup={buildReleaseFeaturesColgroup(releaseMode)}
          colSpan={resolveReleaseFeaturesColumnCount(releaseMode)}
          depth={1}
          header={buildReleaseFeaturesHeader(releaseMode)}
          text="No associated features"
        />
      )}
    </Box>
  </TableV2NestedCollapse>
);
